import {
  type AcatsTransfer,
  getAcatsTransferDetails,
  getAllNonCompletedAcatsTransfers,
} from "../api/rest/acats";
import type {
  AcatsOwnerTransferStorage,
  AcatsTransferBannerData,
  AcatsTransferStoredData,
} from "../types/acats";

const ACATS_STORAGE_KEY = "acats_transfers";
const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * Utilities for managing ACATS transfers in localStorage.
 * Uses a tree structure: owner -> account -> transfers.
 * Safe to call outside of React components.
 */

function getStorage(): AcatsOwnerTransferStorage {
  try {
    const stored = window.localStorage.getItem(ACATS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn("Failed to read ACATS transfers from localStorage:", error);
    return {};
  }
}

function setStorage(storage: AcatsOwnerTransferStorage): void {
  try {
    window.localStorage.setItem(ACATS_STORAGE_KEY, JSON.stringify(storage));
  } catch (error) {
    console.error("Failed to save ACATS transfers to localStorage:", error);
  }
}

function createAcatsTransferLocalStorageEntry(
  acatsTransfer: AcatsTransfer,
): AcatsTransferStoredData {
  const acatsId = acatsTransfer.acats_id;
  const currentTime = new Date();
  const createdAt = currentTime.toISOString();

  return {
    acatsId,
    createdAt,
  };
}

function addTransfer(
  ownerId: string,
  accountId: string,
  acatsTransfer: AcatsTransfer,
): AcatsTransferStoredData {
  const transferData = createAcatsTransferLocalStorageEntry(acatsTransfer);
  const storage = getStorage();

  // Initialize owner if doesn't exist
  if (!storage[ownerId]) {
    storage[ownerId] = {};
  }

  // Initialize account if doesn't exist
  if (!storage[ownerId][accountId]) {
    storage[ownerId][accountId] = {};
  }

  storage[ownerId][accountId][transferData.acatsId] = transferData;
  setStorage(storage);

  return transferData;
}

async function getTransferForDisplay(
  ownerId: string,
  accountId: string,
  transferId: string,
): Promise<AcatsTransferBannerData | null> {
  const storage = getStorage();
  const storedTransfer = storage[ownerId]?.[accountId]?.[transferId];

  if (!storedTransfer) {
    return null;
  }

  return processStoredTransfer(ownerId, accountId, storedTransfer);
}

async function getAccountTransfersForDisplay(
  ownerId: string,
  accountId: string,
  includeDismissed: boolean = false,
): Promise<AcatsTransferBannerData[]> {
  const storage = getStorage();
  const accountTransfers = storage[ownerId]?.[accountId];

  if (!accountTransfers) {
    return [];
  }

  const transfers = Object.values(accountTransfers);
  const validTransfers: AcatsTransferBannerData[] = [];

  // Process each transfer
  for (const storedTransfer of transfers) {
    // Skip dismissed transfers unless explicitly requested
    if (!includeDismissed && storedTransfer.dismissedAt) {
      continue;
    }

    const bannerData = await processStoredTransfer(
      ownerId,
      accountId,
      storedTransfer,
    );

    if (bannerData) {
      validTransfers.push(bannerData);
    }
  }

  return validTransfers;
}

async function getAllOwnerTransfersForDisplay(
  ownerId: string,
): Promise<AcatsTransferBannerData[]> {
  const storage = getStorage();
  const ownerAccounts = storage[ownerId];

  if (!ownerAccounts) {
    return [];
  }

  const validTransfers: AcatsTransferBannerData[] = [];

  // Process each account
  for (const accountId of Object.keys(ownerAccounts)) {
    const accountBannerData = await getAccountTransfersForDisplay(
      ownerId,
      accountId,
    );
    validTransfers.push(...accountBannerData);
  }

  return validTransfers;
}

async function processStoredTransfer(
  ownerId: string,
  accountId: string,
  storedTransfer: AcatsTransferStoredData,
): Promise<AcatsTransferBannerData | null> {
  // If transfer banner has an expiration time, check if it's expired
  if (storedTransfer.expiresAt) {
    const now = new Date();
    const expiresAt = new Date(storedTransfer.expiresAt);

    if (now > expiresAt) {
      // Remove expired transfer
      removeTransfer(ownerId, accountId, storedTransfer.acatsId);
      return null;
    }
  }

  // Fetch the data to allow the banner to be displayed
  try {
    const apiTransfer = await getAcatsTransferDetails(
      accountId,
      storedTransfer.acatsId,
    );

    // if rejected or settled, add an expiry time 5 minutes from now
    if (
      apiTransfer.status.transfer_status === "REJECTED" ||
      apiTransfer.status.transfer_status === "SETTLED"
    ) {
      const storage = getStorage();
      const currentTransfer =
        storage[ownerId]?.[accountId]?.[storedTransfer.acatsId];

      if (currentTransfer && !currentTransfer.expiresAt) {
        currentTransfer.expiresAt = new Date(
          Date.now() + FIVE_MINUTES_MS,
        ).toISOString();

        setStorage(storage);
      }
    }

    return createBannerData(apiTransfer);
  } catch (error) {
    console.error(
      `Failed to fetch ACATS transfer ${storedTransfer.acatsId}:`,
      error,
    );
    // If API call fails, remove the stored transfer to avoid stale data
    // TODO: This may be too aggressive
    removeTransfer(ownerId, accountId, storedTransfer.acatsId);
    return null;
  }
}

function createBannerData(apiTransfer: AcatsTransfer): AcatsTransferBannerData {
  const { title, description, variant } = getBannerContent(apiTransfer);

  return {
    acatsId: apiTransfer.acats_id,
    title,
    description,
    variant,
    status: apiTransfer.status,
    direction: apiTransfer.direction,
    contraBrokerName: apiTransfer.contra_broker_name,
  };
}

function getBannerContent(transfer: AcatsTransfer): {
  title: string;
  description: string;
  variant: "green" | "blue" | "red";
} {
  const { transfer_status } = transfer.status;
  const direction = transfer.direction === "INCOMING" ? "from" : "to";
  const brokerName = transfer.contra_broker_name || "external broker";

  switch (transfer_status) {
    case "PENDING":
    case "IN_PROGRESS":
      return {
        title: "Transfer status updated",
        description: `Your Portfolio transfer ${direction} "${brokerName} - ${transfer.contra_account_number}" is In Progress.`,
        variant: "blue",
      };

    case "SETTLED":
      return {
        title: "Transfer status updated",
        description: `Your Portfolio transfer ${direction} "${brokerName} - ${transfer.contra_account_number}" is Completed!`,
        variant: "green",
      };

    case "REJECTED":
      return {
        title: "Transfer status updated",
        description: `Your Portfolio transfer ${direction} "${brokerName} - ${transfer.contra_account_number}" is Rejected.`,
        variant: "red",
      };

    default:
      return {
        title: "Transfer Status Update",
        description: `Your ACATS transfer ${direction} ${brokerName} status has been updated.`,
        variant: "blue",
      };
  }
}

function removeTransfer(
  ownerId: string,
  accountId: string,
  transferId: string,
): boolean {
  const storage = getStorage();

  if (storage[ownerId]?.[accountId]?.[transferId]) {
    delete storage[ownerId][accountId][transferId];

    // Clean up empty account if no transfers remain
    if (Object.keys(storage[ownerId][accountId]).length === 0) {
      delete storage[ownerId][accountId];
    }

    // Clean up empty owner if no accounts remain
    if (Object.keys(storage[ownerId]).length === 0) {
      delete storage[ownerId];
    }

    setStorage(storage);
    return true;
  }

  return false;
}

function getAccountTransfers(
  ownerId: string,
  accountId: string,
): AcatsTransferStoredData[] {
  const storage = getStorage();
  const accountTransfers = storage[ownerId]?.[accountId];

  if (!accountTransfers) {
    return [];
  }

  return Object.values(accountTransfers);
}

function getOwnerTransfers(ownerId: string): AcatsTransferStoredData[] {
  const storage = getStorage();
  const ownerAccounts = storage[ownerId];

  if (!ownerAccounts) {
    return [];
  }

  const allTransfers: AcatsTransferStoredData[] = [];

  Object.values(ownerAccounts).forEach((accountTransfers) => {
    allTransfers.push(...Object.values(accountTransfers));
  });

  return allTransfers;
}

function hasOwnerTransfers(ownerId: string): boolean {
  const storage = getStorage();
  return !!storage[ownerId] && Object.keys(storage[ownerId]).length > 0;
}

function getAllTransfers(): AcatsTransferStoredData[] {
  const storage = getStorage();
  const allTransfers: AcatsTransferStoredData[] = [];

  Object.values(storage).forEach((ownerAccounts) => {
    Object.values(ownerAccounts).forEach((accountTransfers) => {
      allTransfers.push(...Object.values(accountTransfers));
    });
  });

  return allTransfers;
}

async function initializeOwnerTransfersFromApi(
  ownerId: string,
  accountIds: string[],
): Promise<void> {
  // Only initialize if owner has no existing transfers
  if (hasOwnerTransfers(ownerId)) {
    return;
  }

  try {
    const activeTransfers = await getAllNonCompletedAcatsTransfers(accountIds);

    // Add each transfer to localStorage
    for (const transfer of activeTransfers) {
      addTransfer(ownerId, transfer.account_id, transfer);
    }
  } catch (error) {
    console.error("Failed to initialize ACATS transfers from API:", error);
    // Don't throw - this is a best-effort initialization
  }
}

function removeExpiredTransfers(): number {
  const storage = getStorage();
  const now = new Date();
  let removedCount = 0;

  Object.keys(storage).forEach((ownerId) => {
    Object.keys(storage[ownerId]).forEach((accountId) => {
      Object.values(storage[ownerId][accountId]).forEach((transfer) => {
        // Only check expiration if expiresAt is defined
        if (transfer.expiresAt) {
          const expiresAt = new Date(transfer.expiresAt);
          if (now > expiresAt) {
            delete storage[ownerId][accountId][transfer.acatsId];
            removedCount++;
          }
        }
      });

      // Clean up empty account
      if (Object.keys(storage[ownerId][accountId]).length === 0) {
        delete storage[ownerId][accountId];
      }
    });

    // Clean up empty owner
    if (Object.keys(storage[ownerId]).length === 0) {
      delete storage[ownerId];
    }
  });

  if (removedCount > 0) {
    setStorage(storage);
  }

  return removedCount;
}

function dismissAccountTransfers(ownerId: string, accountId: string): boolean {
  const storage = getStorage();
  const accountTransfers = storage[ownerId]?.[accountId];

  if (!accountTransfers) {
    return false;
  }

  const dismissedAt = new Date().toISOString();
  let hasChanges = false;

  // Mark all transfers in this account as dismissed
  Object.values(accountTransfers).forEach((transfer) => {
    if (!transfer.dismissedAt) {
      transfer.dismissedAt = dismissedAt;
      hasChanges = true;
    }
  });

  if (hasChanges) {
    setStorage(storage);
  }

  return hasChanges;
}

export const AcatsTransferManager = {
  addTransfer,
  getTransferForDisplay,
  getAccountTransfersForDisplay,
  getAllOwnerTransfersForDisplay,
  removeTransfer,
  getAccountTransfers,
  getOwnerTransfers,
  hasOwnerTransfers,
  getAllTransfers,
  initializeOwnerTransfersFromApi,
  removeExpiredTransfers,
  dismissAccountTransfers,
};
